import React, { useEffect, useState, useMemo } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, AreaChart, Area, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import { useWS } from '../context/WSContext';
import { useNavigate } from 'react-router-dom';
import { useTwin } from '../context/TwinContext';
import { alertStore } from '../utils/alertStore';
import './Dashboard.css';

// ── Sparkline history buffer per sensor ────────────────────────
const historyMap: Record<string, number[]> = {};
const MAX_POINTS = 20;

const getHistory = (sensorId: string, value: number) => {
  if (!historyMap[sensorId]) historyMap[sensorId] = [];
  historyMap[sensorId].push(value);
  if (historyMap[sensorId].length > MAX_POINTS) historyMap[sensorId].shift();
  return historyMap[sensorId].map((v, i) => ({ t: i, v }));
};

// ── Sensor health helpers ──────────────────────────────────────
const sensorPct = (val: number, min: number, max: number) =>
  max === min ? 0 : Math.max(0, Math.min(100, ((val - min) / (max - min)) * 100));

const sensorStatus = (pct: number): 'ok' | 'warn' | 'crit' =>
  pct > 95 ? 'crit' : pct > 80 ? 'warn' : 'ok';

const statusColor = (s: string) =>
  s === 'crit' ? '#f87171' : s === 'warn' ? '#fbbf24' : '#34d399';

// ── Fleet health ring data ─────────────────────────────────────
const fleetRingData = (machines: any[]) => {
  const counts = { running: 0, fault: 0, idle: 0 };
  machines.forEach(m => counts[m.status as keyof typeof counts]++);
  return [
    { name: 'Running', value: counts.running, fill: '#34d399' },
    { name: 'Fault', value: counts.fault, fill: '#f87171' },
    { name: 'Idle', value: counts.idle, fill: '#6b7280' },
  ].filter(d => d.value > 0);
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { connected } = useWS();
  const { getActiveTwin, isDemo } = useTwin();
  const activeTwin = getActiveTwin();
  const [tick, setTick] = useState(0);
  const generatePoint = () => ({
    time:   new Date().toLocaleTimeString(),
    volume: +(200 + Math.random() * 300).toFixed(1),
    flow:   +(20 + Math.random() * 60).toFixed(1),
  });

  const [telemetry, setTelemetry] = useState<{ time: string; volume: number; flow: number }[]>(
    () => Array.from({ length: 10 }, generatePoint)
  );

  // Re-render every 2s — push new data point
  useEffect(() => {
    const iv = setInterval(() => {
      setTick(t => t + 1);
      setTelemetry(prev => [...prev.slice(-19), generatePoint()]);
    }, 2000);
    return () => clearInterval(iv);
  }, []);

  const machines = useMemo(() => activeTwin?.machines || [], [activeTwin, tick]);
  const simData   = activeTwin?.simulatedMachineData || {};
  const healthScore = getActiveTwin()?.healthScore ?? 100;

  // Build sparkline data for each sensor
  const sensorSnapshots = useMemo(() => {
    const map: Record<string, { val: number; pct: number; status: string; history: { t: number; v: number }[] }> = {};
    machines.forEach(m => {
      m.sensors.forEach((s: any) => {
        const val = simData[m.machineId]?.[s.sensorId] ?? s.value;
        const pct = sensorPct(val, s.min, s.max);
        map[s.sensorId] = {
          val, pct,
          status: sensorStatus(pct),
          history: getHistory(s.sensorId, val),
        };
      });
    });
    return map;
  }, [machines, simData, tick]);

  const [alertTick, setAlertTick] = useState(0);
  useEffect(() => {
    const unsub = alertStore.subscribe(() => setAlertTick(t => t + 1));
    return () => { unsub(); };
  }, []);

  // KPIs — use alertStore for consistent counts with Alerts page
  const totalSensors = machines.reduce((n: number, m: any) => n + (m.sensors?.length || 0), 0);
  const faultCount   = machines.filter((m: any) => m.status === 'fault').length;
  const activeAlerts = alertStore.getAlerts();
  const critSensors  = activeAlerts.filter(a => a.severity === 'Critical' || a.severity === 'High').length;
  const warnSensors  = activeAlerts.filter(a => a.severity === 'Medium' || a.severity === 'Low').length;
  const ringData = fleetRingData(machines);

  if (!activeTwin) {
    return (
      <div className="dashboard" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 32, marginBottom: 16 }}>🏭</div>
          <p>Welcome! Upload a 3D model to get started.</p>
          <button className="btn-primary" onClick={() => navigate('/upload-model')} style={{ marginTop: 16 }}>
            📦 Upload 3D Model →
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      {/* Header */}
      <div className="page-header">
        <h1>🏭 {activeTwin.company || 'Digital Twin'} Dashboard</h1>
        <span className={`mode-pill ${isDemo ? 'demo' : 'live'}`}>
          {isDemo ? '📌 Demo' : '✨ Live'}
        </span>
        <span className={connected ? 'badge-green' : 'badge-yellow'}>
          {connected ? '⚡ WS Live' : ''}
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button className="btn-primary" style={{ fontSize: 13 }} onClick={() => navigate('/upload-model')}>📦 Upload 3D Model</button>
          <button className="btn-secondary" style={{ fontSize: 13 }} onClick={() => navigate('/app/scene')}>🎬 3D Scene →</button>
          <button className="btn-secondary" style={{ fontSize: 13 }} onClick={() => navigate('/app/ml')}>🤖 ML Insights →</button>
        </div>
      </div>

      {/* KPI Strip */}
      <div className="kpi-grid">
        {[
          { label: 'Machines', value: machines.length, icon: '⚙️', color: '#a78bfa' },
          { label: 'Sensors', value: totalSensors, icon: '📡', color: '#00d4ff' },
          { label: 'Health', value: `${healthScore}%`, icon: '💚', color: healthScore >= 80 ? '#34d399' : healthScore >= 50 ? '#fbbf24' : '#f87171' },
          { label: 'Faults', value: faultCount, icon: '🔴', color: '#f87171' },
          { label: 'Critical', value: critSensors, icon: '⚠️', color: '#f97316' },
          { label: 'Warnings', value: warnSensors, icon: '🟡', color: '#fbbf24' },
        ].map((k, i) => (
          <div className="kpi-card card" key={i}>
            <div className="kpi-icon">{k.icon}</div>
            <div className="kpi-value" style={{ color: k.color }}>{k.value}</div>
            <div className="kpi-label">{k.label}</div>
          </div>
        ))}
      </div>

      {/* Fleet Health Ring + Anomalies */}
      <div className="fleet-row">
        <div className="card fleet-ring-card">
          <h3>🏭 Fleet Status</h3>
          <div className="fleet-ring-wrap">
            <ResponsiveContainer width={160} height={160}>
              <PieChart>
                <Pie data={ringData} dataKey="value" cx="50%" cy="50%" innerRadius={48} outerRadius={68} strokeWidth={0}>
                  {ringData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className="fleet-ring-center">
              <span className="frc-val">{machines.length}</span>
              <span className="frc-lbl">machines</span>
            </div>
          </div>
          <div className="fleet-legend">
            {ringData.map(d => (
              <span key={d.name} className="fl-item">
                <span className="fl-dot" style={{ background: d.fill }} />
                {d.name}: {d.value}
              </span>
            ))}
          </div>
        </div>

        {/* Anomalies */}
        <div className="card anomaly-card">
          <h3>⚡ Live Anomalies</h3>
          {(() => {
            const simAlerts = alertStore.getAlerts().filter(a => a.source === 'whatif');
            const twinAnomalies = activeTwin.anomalies || [];
            if (simAlerts.length === 0 && twinAnomalies.length === 0) {
              return <p className="no-anomaly">✅ All sensors within normal range</p>;
            }
            return (
              <div className="anomaly-list">
                {simAlerts.map((a, i) => {
                  const color = a.severity === 'Critical' ? '#f87171' : a.severity === 'Medium' ? '#f97316' : '#60a5fa';
                  const icon  = a.severity === 'Critical' ? '🔴' : a.severity === 'Medium' ? '🟠' : '🔵';
                  return (
                    <div key={a.id} className="anomaly-row" style={{ color, borderLeft: `3px solid ${color}`, paddingLeft: 8 }}>
                      {icon} {a.machineName} — {a.recommendedAction}
                    </div>
                  );
                })}
                {twinAnomalies.slice(0, Math.max(0, 6 - simAlerts.length)).map((a: string, i: number) => (
                  <div key={i} className="anomaly-row">{a}</div>
                ))}
              </div>
            );
          })()}
        </div>

        {/* Quick Actions */}
        <div className="card quick-actions-card">
          <h3>🚀 Quick Actions</h3>
          <div className="qa-grid">
            {[
              { label: 'Upload 3D', icon: '📦', path: '/upload-model' },
              { label: '3D Scene', icon: '🎬', path: '/app/scene' },
              { label: 'ML Insights', icon: '🤖', path: '/app/ml' },
              { label: 'Alerts', icon: '🔔', path: '/app/alerts' },
              { label: 'Reports', icon: '📋', path: '/app/reports' },
            ].map(q => (
              <button key={q.label} className="qa-btn" onClick={() => navigate(q.path)}>
                <span>{q.icon}</span>
                <span>{q.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Live Charts — Volume & Flow Rate */}
      <div className="charts-grid">
        <div className="card chart-card">
          <h3>🛢️ Tank Volume</h3>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={telemetry}>
              <defs>
                <linearGradient id="volGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#00d4ff" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#00d4ff" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
              <XAxis dataKey="time" stroke="#6b7280" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
              <YAxis stroke="#6b7280" tick={{ fontSize: 10 }} unit=" L" />
              <Tooltip contentStyle={{ background: '#111827', border: '1px solid #1f2937', borderRadius: 8 }} formatter={(v: any) => [`${v} L`, 'Tank Volume']} />
              <Area type="monotone" dataKey="volume" stroke="#00d4ff" fill="url(#volGrad)" strokeWidth={2} dot={false} name="Tank Volume" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="card chart-card">
          <h3>💧 Flow Rate</h3>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={telemetry}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
              <XAxis dataKey="time" stroke="#6b7280" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
              <YAxis stroke="#6b7280" tick={{ fontSize: 10 }} unit=" L/m" />
              <Tooltip contentStyle={{ background: '#111827', border: '1px solid #1f2937', borderRadius: 8 }} formatter={(v: any) => [`${v} L/min`, 'Flow Rate']} />
              <Line type="monotone" dataKey="flow" stroke="#34d399" strokeWidth={2} dot={false} name="Flow Rate" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Machine List */}
      <div className="dashboard-mid">
        <div className="card" style={{ flex: 1 }}>
          <h3 style={{ marginBottom: 16 }}>⚙️ Machines ({machines.length})</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {machines.map((m: any) => (
              <div key={m.machineId} className={`machine-row ms-${m.status}`} onClick={() => navigate('/app/entities')}>
                <div className="mr-left">
                  <span className={`mc-dot dot-${m.status}`} />
                  <strong>{m.name}</strong>
                  <span className="mc-type">{m.type}</span>
                </div>
                <div className="mr-right">
                  <span className="mr-sensors">{m.sensors?.length || 0} sensors</span>
                  <span className={`mc-status-badge sb-${m.status}`}>
                    {m.status === 'running' ? '● Running' : m.status === 'fault' ? '● Fault' : '○ Idle'}
                  </span>
                </div>
              </div>
            ))}
            {machines.length === 0 && <p style={{ color: '#6b7280', fontSize: 13 }}>No machines configured</p>}
          </div>
        </div>
        <div className="card" style={{ flex: 1 }}>
          <h3 style={{ marginBottom: 16 }}>📊 Sensor Overview</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {machines.map((m: any) => (
              <div key={m.machineId} className="sensor-overview-row">
                <span className="sor-name">{m.name}</span>
                <div className="sor-chips">
                  {(m.sensors || []).map((s: any) => {
                    const snap = sensorSnapshots[s.sensorId];
                    const sc = snap ? statusColor(snap.status) : '#6b7280';
                    return (
                      <span key={s.sensorId} className="sor-chip" style={{ borderColor: sc, color: sc }}>
                        {s.name}: {snap ? snap.val.toFixed(1) : '—'}{s.unit}
                      </span>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Workspace Info */}
      <div className="card ws-info-card">
        <h3>📋 Workspace</h3>
        <div className="ws-info-grid">
          <div><span className="wi-label">ID</span><span className="wi-val mono">{activeTwin.id}</span></div>
          <div><span className="wi-label">Company</span><span className="wi-val">{activeTwin.company}</span></div>
          <div><span className="wi-label">Type</span><span className="wi-val cap">{activeTwin.factoryType}</span></div>
          <div><span className="wi-label">Health</span><span className="wi-val" style={{ color: healthScore >= 80 ? '#34d399' : '#f87171' }}>{healthScore}%</span></div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
